import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import {useEffect, useRef} from 'react';
import {ApiConfig} from '@/config/api.config.ts';
import {useAuthUser} from "@/features/identity/hooks/useAuthUser.ts";
import {useAuthTokens} from "@/features/identity/hooks/useAuthTokens.ts";
import {useSignOut} from "@/features/identity/hooks/useSignOut.ts";
import {useHotelStorage} from "@/features/hotels/hooks/useHotelStorage.ts";
import {TokensResponseDto} from "@/@types/auth.types.ts";

// let axiosAuthInstance: AxiosInstance | null = null;

export const getAxiosAuthInstance = (): AxiosInstance => {
    // if (!axiosAuthInstance) {
    // 	axiosAuthInstance = axios.create({
    // 		baseURL: ApiConfig.currentUrls.api,
    // 		// any other defaults like timeout, headers, etc
    // 	});
    // }
    // return axiosAuthInstance;
    return ApiConfig.axiosInstance;
};

// Single-flight refresh: while one refresh is in progress, queue every other 401 onto the same
// promise so we only ever hit the refresh endpoint once.
let refreshPromise: Promise<TokensResponseDto | null> | null = null;

const refreshTokens = async (): Promise<TokensResponseDto | null> => {
    const currentTokens = useAuthTokens.getState().tokens;
    if (!currentTokens?.refreshToken) return null;

    const axiosInstance = axios.create({baseURL: ApiConfig.currentUrls.api});
    const response = await axiosInstance.post<TokensResponseDto>(
        ApiConfig.queries.auth.refreshToken.endpoint,
        {refreshToken: currentTokens.refreshToken},
    );

    const tokens = response.data;
    useAuthTokens.getState().setTokens(tokens);
    return tokens;
};

export const useAxiosAuth = (): AxiosInstance => {
    const signOut = useSignOut();
    const instance = getAxiosAuthInstance();
    const signOutRef = useRef(signOut);
    signOutRef.current = signOut;

    useEffect(() => {
        // Read auth context from Zustand on every request so x-hotel-id matches the current
        // selection immediately (React effect runs after paint; invalidateQueries can refetch sooner).
        const requestInterceptorId = instance.interceptors.request.use((config) => {
            const tokens = useAuthTokens.getState().tokens;
            const tenant = useAuthUser.getState().tenant;
            const selectedHotel = useHotelStorage.getState().selectedHotel;
            //@ts-ignore
            config.headers = {
                ...config.headers,
                ...(tokens?.accessToken && { Authorization: `Bearer ${tokens.accessToken}` }),
                ...(tenant?.id && { 'x-tenant-id': tenant.id }),
                ...(selectedHotel?.id && { 'x-hotel-id': selectedHotel.id }),
            };
            return config;
        }, (error) => Promise.reject(error));

        const responseInterceptorId = instance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error?.config as
                    | (AxiosRequestConfig & {_retry?: boolean})
                    | undefined;

                const isRefreshCall = originalRequest?.url?.includes(
                    ApiConfig.queries.auth.refreshToken.endpoint,
                );

                if (
                    error.response?.status === 401 &&
                    originalRequest &&
                    !originalRequest._retry &&
                    !isRefreshCall &&
                    useAuthTokens.getState().tokens?.refreshToken
                ) {
                    originalRequest._retry = true;

                    try {
                        // Reuse an in-flight refresh if one is already running.
                        refreshPromise = refreshPromise ?? refreshTokens();
                        const tokens = await refreshPromise;
                        refreshPromise = null;

                        if (tokens?.accessToken) {
                            originalRequest.headers = {
                                ...originalRequest.headers,
                                Authorization: `Bearer ${tokens.accessToken}`,
                            };
                            return instance(originalRequest);
                        }
                    } catch {
                        refreshPromise = null;
                    }

                    // Refresh failed (or no new token): fall through to sign-out.
                    signOutRef.current();
                    window.location.href = ApiConfig.unAuthenticatedEntryPath;
                    return Promise.reject(error);
                }

                if (error.response && error.response.status === 401) {
                    signOutRef.current();
                    window.location.href = ApiConfig.unAuthenticatedEntryPath;
                }
                return Promise.reject(error);
            },
        );

        return () => {
            instance.interceptors.request.eject(requestInterceptorId);
            instance.interceptors.response.eject(responseInterceptorId);
        };
    }, [instance]);

    return instance;
};
